/**
 * Application entry point.
 *
 * Provider order (outermost → innermost):
 *
 *   1. HelmetProvider   — react-helmet-async for <head> management
 *   2. LanguageProvider — i18next with 5 languages + RTL support
 *   3. ThemeProvider    — light / dark mode via .dark class
 *   4. AuthProvider     — JWT token lifecycle (store, refresh, expire)
 *   5. QueryProvider    — TanStack Query client
 *   6. RouterProvider   — TanStack Router with generated routes
 *
 * Why this order:
 * - Helmet is language-independent, so it wraps everything.
 * - Language wraps theme so that theme-toggle labels are translatable.
 * - Theme wraps auth because theme applies regardless of auth state.
 * - Auth wraps query so that query functions can read the access token.
 * - Query wraps the router so that route loaders can prefetch data.
 */
import React from "react";
import { createRoot } from "react-dom/client";
import {
  createRootRoute,
  createRoute,
  createRouter,
  notFound,
  redirect,
  RouterProvider,
  useNavigate,
  useLocation,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";

import { LanguageProvider } from "./providers/LanguageProvider";
import { ThemeProvider } from "./providers/ThemeProvider";
import { AuthProvider, useAuth } from "@/providers/AuthProvider";
import { QueryProvider, getQueryClient } from "./providers/QueryProvider";
import {
  changeLanguage as setI18nLanguage,
  getCurrentLanguage,
  supportedLangs,
  type SupportedLanguage,
} from "./i18n";
import { App } from "./App";
import {
  FEATURE_ROUTE_PREFIXES,
  routes as generatedRoutes,
  type Route as GeneratedRoute,
} from "./routes";
import { SystemShell, useVisibleRoutes } from "@shared/layouts/system-shell";
import { siteFeatureFlagKeys } from "@shared/hooks/use-site-feature-flags";
import { useLandingPath } from "@shared/hooks/use-site-feature-flags/useLandingPath";
import { configureFeatureRoutes } from "@shared/lib/site-features";
import { FEATURE_DISABLED_EVENT } from "@shared/lib/api/client";
import { MobileShell } from "@shared/layouts/mobile-shell/MobileShell";
import { MobileMenuNav } from "@shared/layouts/mobile-shell/MobileMenuNav";
import { useMediaQuery } from "@shared/hooks/use-media-query/useMediaQuery";
import { OfflineBanner } from "./components/offline/OfflineBanner";
import { InstallPromptBanner } from "./components/install-prompt/InstallPromptBanner";
import {
  getRouteDocumentTitle,
  RouteDocumentMeta,
} from "./components/RouteDocumentMeta";
// The router's own availability guard. It reads the public config over a raw
// `fetch` (no provider is mounted yet in `beforeLoad`) and shares the prefix
// tables in `@shared/lib/paywall` with the SystemShell nav filter.
import {
  enforceFeatureRouteAvailability,
  enforcePaymentRouteAvailability,
  localizedCurrentRoot,
} from "./routeAvailability";
import { CommunityShell } from "@apps/community/frontend/components/CommunityShell";
import { WalletHeaderControls } from "@apps/payment/frontend/components/wallet/WalletHeaderControls";
import { LandingPage } from "@shared/landing/LandingPage";
import { ProductPrivacyPage, ProductTermsPage, ProductMedicalDisclaimerPage } from "./legal-pages";
import { LegalAcceptanceGate } from "@shared/legal/legal-acceptance-gate/LegalAcceptanceGate";
import { registerChunkLoadErrorHandler } from "./app-load-recovery";
import { registerSW } from "./pwa/registerSW";
import { native } from "./native/index";
import { mountNativeLifecycle } from "./native/lifecycle";
import { initPushNotifications } from "./native/push";
import { initWebPushIfGranted } from "@shared/lib/native/webPush";
import { initAnalytics } from "@shared/lib/native/analytics";
import { initScheduler } from "./native/scheduler";
import { tokenStore } from "./auth/tokenStore";
import { setTokenStore } from "@shared/lib/api/client";
import { GuestModeProvider } from "./providers/GuestModeProvider";
import { ROUTER_PENDING_OPTIONS } from "./router-pending";
import { getStableRouteComponent } from "./lib/route-identity";
import { GuestBanner } from "./components/guest-mode/GuestBanner";
import { RequireAuthProvider } from "@shared/lib/auth-gating";
import { getAuthLegalHref } from "./authLegalHref";
import { getAuthRouteHref } from "./authRouteHref";
import { getNotFoundHomeHref } from "./notFoundHomeHref";
import { getPostLoginDestination } from "./postLoginDestination";
import {
  getAuthenticatedDestination,
  type AuthSuccessFlow,
} from "./authSuccessDestination";
import {
  isSafeReturnPath,
  localizedAuthPath,
} from "./lib/guestMode/returnUrl";
import "./globals.css";

// One generated table (routes.ts, from the backend feature registry) feeds
// every flag consumer: nav, router guard, landing page, href lookups.
configureFeatureRoutes({ prefixes: FEATURE_ROUTE_PREFIXES, routes: generatedRoutes });

// A request hit a feature the admin just switched off → refetch the flags so
// the stale link disappears (the api client collapses bursts into one event).
if (typeof window !== "undefined") {
  window.addEventListener(FEATURE_DISABLED_EVENT, () => {
    void getQueryClient().invalidateQueries({ queryKey: siteFeatureFlagKeys.all });
  });
}

// Register the platform-aware token store before any auth-relying code runs.
// On native, this swaps the access-token store from localStorage to in-memory
// and routes the refresh token through the OS keychain via Capacitor Preferences.
setTokenStore(tokenStore);

registerChunkLoadErrorHandler();

if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  registerSW();
}

// ── Native lifecycle (Capacitor) ────────────────────────────────────
// Mounted only inside native apps (iOS / Android). On web this block is
// skipped entirely so no Capacitor bridge calls happen in the browser.
// The actual mounting happens after the router/queryClient are constructed
// below; this flag captures native state at module load time.
const IS_NATIVE = native.isNative;

// ── Sentry initialisation ──────────────────────────────────────────
// Deferred to after first paint so it doesn't block the critical path.
import { scrubSentryEvent } from "@shared/lib/sentry-scrub";

const sentryInitOptions: Sentry.BrowserOptions = {
  dsn: import.meta.env.VITE_SENTRY_DSN || "",
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  // Strip Authorization headers and token-like values from every event so
  // a leaked DSN can never exfiltrate a session token. See
  // `@shared/lib/sentry-scrub` for the scrub contract.
  beforeSend: (event) => scrubSentryEvent(event) as typeof event,
  beforeBreadcrumb: (breadcrumb) =>
    scrubSentryEvent(breadcrumb) as typeof breadcrumb,
};

if ("requestIdleCallback" in window) {
  requestIdleCallback(() => Sentry.init(sentryInitOptions));
} else {
  setTimeout(() => Sentry.init(sentryInitOptions), 2000);
}

// ── Auth-page adapter ──────────────────────────────────────────────
// Auth pages (layout === "auth") expect navigation callbacks and token
// handlers that only the consumer can supply. Wrap them so they get:
//   - onNavigateToLogin / Register / ForgotPassword via TanStack Router
//   - onLoginSuccess / onRegisterSuccess that persist tokens via AuthProvider
//   - token / initialEmail derived from URL params + search

interface AuthSuccessResponse {
  accessToken?: string;
  refreshToken?: string;
  isEmailVerified?: boolean;
  message?: string;
  user?: { email?: string };
}

const isSupportedLanguage = (value?: string): value is SupportedLanguage =>
  !!value && (supportedLangs as readonly string[]).includes(value);


function useRouteLanguageSync() {
  const { lang } = useParams({ strict: false }) as { lang?: string };

  React.useEffect(() => {
    if (!isSupportedLanguage(lang)) return;
    if (getCurrentLanguage() !== lang) {
      void setI18nLanguage(lang);
    }
  }, [lang]);
}

function AuthRouteAdapter({
  Component,
  routeName,
}: {
  Component: React.ComponentType<Record<string, unknown>>;
  routeName: string;
}) {
  useRouteLanguageSync();

  const navigate = useNavigate();
  const { login: persistTokens } = useAuth();
  const params = useParams({ strict: false }) as { token?: string };
  const search = useSearch({ strict: false }) as { email?: string };

  // TanStack Router's `to` is a typed union of registered routes; our routes
  // come from a generated module so we cast through `unknown` for now.
  const navTo = (path: string) =>
    navigate({ to: path as unknown as "." } as never);
  const goLogin = React.useCallback(() => navTo(getAuthRouteHref("login")), [navigate]);
  const goRegister = React.useCallback(() => navTo(getAuthRouteHref("register")), [navigate]);
  const goForgotPassword = React.useCallback(
    () => navTo(getAuthRouteHref("forgot-password")),
    [navigate],
  );
  const goTerms = React.useCallback(() => navTo(getAuthLegalHref("terms")), [navigate]);
  const goPrivacy = React.useCallback(
    () => navTo(getAuthLegalHref("privacy")),
    [navigate],
  );
  const handleAuthSuccess = React.useCallback(
    (flow: AuthSuccessFlow, response?: AuthSuccessResponse) => {
      if (response?.accessToken && response?.refreshToken) {
        persistTokens(response.accessToken, response.refreshToken);
        navTo(getAuthenticatedDestination(flow));
        return;
      }
      if (response?.isEmailVerified === false && response.user?.email) {
        navigate({
          to: "/verify-email" as unknown as ".",
          search: { email: response.user.email } as never,
        } as never);
        return;
      }
      goLogin();
    },
    [navigate, persistTokens, goLogin],
  );

  const handleLoginSuccess = React.useCallback(
    (response?: AuthSuccessResponse) => handleAuthSuccess("login", response),
    [handleAuthSuccess],
  );
  const handleRegisterSuccess = React.useCallback(
    (response?: AuthSuccessResponse) => handleAuthSuccess("register", response),
    [handleAuthSuccess],
  );

  const props: Record<string, unknown> = {
    onNavigateToLogin: goLogin,
    onNavigateToRegister: goRegister,
    onNavigateToForgotPassword: goForgotPassword,
    onLoginSuccess: handleLoginSuccess,
    onRegisterSuccess: handleRegisterSuccess,
    onTermsClick: goTerms,
    onPrivacyClick: goPrivacy,
    token: params.token,
    initialEmail: search.email,
  };

  // Mark routeName as used so the type-check stays clean even if we add
  // page-specific branches later.
  void routeName;

  return <Component {...props} />;
}

// ── Router ─────────────────────────────────────────────────────────
// Routes come from `frontend/src/routes.ts`, which is auto-generated by
// `python manage.py generate_frontend_routes` from each Django app's
// `frontend_patterns`. We translate the Django-style ":param" path syntax
// into TanStack Router's "$param" form before constructing the tree.
const toTanstackPath = (p: string): string => p.replace(/:(\w+)/g, "$$$1");

// Auth-landing routes (login/register) bounce already-authenticated users to
// the dashboard so they don't re-see the form after a successful login.
const AUTH_LANDING_ROUTE_NAMES = new Set([
  "login",
  "login-i18n",
  "register",
  "register-i18n",
]);

function NotFoundPage() {
  const { t } = useTranslation("errors");
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <section className="max-w-md space-y-4 text-center">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          404
        </p>
        <h1 className="text-3xl font-bold">
          {t("pages.notFound.title", "Page Not Found")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            "pages.notFound.description",
            "The page you are looking for doesn't exist or has been moved.",
          )}
        </p>
        <a
          href={getNotFoundHomeHref()}
          className="inline-flex min-h-11 items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          {t("pages.notFound.goHome", "Go Home")}
        </a>
      </section>
    </main>
  );
}

function DocsPage() {
  useRouteLanguageSync();
  const { t } = useTranslation("common");
  const params = useParams({ strict: false }) as { _splat?: string };
  const landingPath = useLandingPath();
  const segments = (params._splat ?? "").split("/").filter(Boolean);
  const title =
    segments.at(-1)?.replace(/[-_]+/g, " ") ||
    t("docsHelpLink.documentation", "Documentation");

  return (
    <main className="min-h-screen bg-background px-4 py-8 text-foreground sm:px-6 lg:px-8">
      <section className="mx-auto max-w-3xl rounded-xl border bg-card p-6 shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t("docsHelpLink.documentation", "Documentation")}
        </p>
        <h1 className="mt-2 text-2xl font-bold capitalize sm:text-3xl">
          {title}
        </h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          {t(
            "docsHelpLink.pageDescription",
            "This guide is available inside the platform so help links never leave you on a broken page.",
          )}
        </p>
        <a
          href={landingPath}
          className="mt-6 inline-flex min-h-11 items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          {t("nav.dashboard", "Dashboard")}
        </a>
      </section>
    </main>
  );
}

const rootRoute = createRootRoute({
  component: App,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: LandingPage,
});

const localizedIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$lang",
  beforeLoad: ({ params }) => {
    const lang = (params as { lang?: string }).lang;
    if (!isSupportedLanguage(lang)) {
      throw notFound();
    }
  },
  component: function LocalizedLandingPage() {
    useRouteLanguageSync();
    return <LandingPage />;
  },
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: ProductTermsPage,
});

const localizedTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$lang/terms",
  beforeLoad: ({ params }) => {
    const lang = (params as { lang?: string }).lang;
    if (!isSupportedLanguage(lang)) {
      throw notFound();
    }
  },
  component: function LocalizedTermsPage() {
    useRouteLanguageSync();
    return <ProductTermsPage />;
  },
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: ProductPrivacyPage,
});

const localizedPrivacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$lang/privacy",
  beforeLoad: ({ params }) => {
    const lang = (params as { lang?: string }).lang;
    if (!isSupportedLanguage(lang)) {
      throw notFound();
    }
  },
  component: function LocalizedPrivacyPage() {
    useRouteLanguageSync();
    return <ProductPrivacyPage />;
  },
});

const medicalDisclaimerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/medical-disclaimer",
  component: ProductMedicalDisclaimerPage,
});

const localizedMedicalDisclaimerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$lang/medical-disclaimer",
  beforeLoad: ({ params }) => {
    const lang = (params as { lang?: string }).lang;
    if (!isSupportedLanguage(lang)) {
      throw notFound();
    }
  },
  component: function LocalizedMedicalDisclaimerPage() {
    useRouteLanguageSync();
    return <ProductMedicalDisclaimerPage />;
  },
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/$",
  component: DocsPage,
});

const localizedDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$lang/docs/$",
  beforeLoad: ({ params }) => {
    const lang = (params as { lang?: string }).lang;
    if (!isSupportedLanguage(lang)) {
      throw notFound();
    }
  },
  component: DocsPage,
});

function createLegacyRedirectRoute(path: string, target: string) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to: target as unknown as "/" });
    },
    component: () => null,
  });
}

const legacyRedirectRoutes = [
  createLegacyRedirectRoute("/profile/protocol", "/profile/edit"),
  createLegacyRedirectRoute("/$lang/profile/protocol", "/profile/edit"),
  createLegacyRedirectRoute("/profile/conditions", "/profile/edit"),
  createLegacyRedirectRoute("/$lang/profile/conditions", "/profile/edit"),
  createLegacyRedirectRoute("/settings/privacy", "/settings"),
  createLegacyRedirectRoute("/$lang/settings/privacy", "/settings"),
  createLegacyRedirectRoute("/treatments/journal", "/treatments/log"),
  createLegacyRedirectRoute("/$lang/treatments/journal", "/treatments/log"),
  createLegacyRedirectRoute("/legal/medical-disclaimer", "/medical-disclaimer"),
  createLegacyRedirectRoute(
    "/$lang/legal/medical-disclaimer",
    "/medical-disclaimer",
  ),
  createLegacyRedirectRoute("/community/profile/$handle", "/community/search"),
  createLegacyRedirectRoute(
    "/$lang/community/profile/$handle",
    "/community/search",
  ),
];

// Community routes that receive the responsive CommunityShell wrapper.
// /community/share/:token uses layout "minimal" so it is already excluded.
function isCommunityShellRoute(r: GeneratedRoute): boolean {
  if (r.layout !== "dashboard") return false;
  const path = r.path.startsWith("/:lang")
    ? r.path.slice("/:lang".length) || "/"
    : r.path;
  return (
    path === "/hope" ||
    path.startsWith("/hope/") ||
    path === "/gratitude" ||
    path === "/similar-users" ||
    (path.startsWith("/community") && !path.startsWith("/community/share"))
  );
}

function DashboardLayout({
  r,
  PageComponent,
  useCommunityShellWrapper,
}: {
  r: GeneratedRoute;
  PageComponent: React.ComponentType;
  useCommunityShellWrapper: boolean;
}) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("common");

  const handleLogout = React.useCallback(() => {
    logout();
    void navigate({ to: "/login" as unknown as "." } as never);
  }, [logout, navigate]);

  // Same pipeline as the desktop sidebar (flags → paywall → admin → the
  // user's hidden groups), so the mobile menu never lists a disabled feature
  // and honours hidden groups too. Only the mobile branch needs the items.
  const { items: mobileNavItems } = useVisibleRoutes({
    routes: generatedRoutes,
    user,
    withSidebarPreferences: isMobile,
  });
  const mobileTitle = getRouteDocumentTitle(r, t);

  if (isMobile) {
    return (
      <MobileShell
        title={mobileTitle}
        fullBleed={r.navFullBleed === true}
        installPromptBanner={<InstallPromptBanner />}
        secondaryNavContent={
          <MobileMenuNav
            items={mobileNavItems}
            pathname={location.pathname}
            user={user}
            onLogout={handleLogout}
          />
        }
      >
        <RouteDocumentMeta route={r} />
        {useCommunityShellWrapper ? (
          <CommunityShell>
            <PageComponent />
          </CommunityShell>
        ) : (
          <PageComponent />
        )}
      </MobileShell>
    );
  }

  return (
    <SystemShell
      routes={generatedRoutes}
      currentRoute={r}
      user={user}
      onLogout={handleLogout}
      headerExtra={<WalletHeaderControls />}
    >
      <RouteDocumentMeta route={r} />
      {useCommunityShellWrapper ? (
        <CommunityShell>
          <PageComponent />
        </CommunityShell>
      ) : (
        <PageComponent />
      )}
    </SystemShell>
  );
}

// Payment routes that should render inside the dashboard shell (sidebar +
// dashboard header) for authenticated users instead of their declared
// public/minimal layout, so the chrome matches the rest of the app. Logged-out
// visitors keep the public/minimal layout. /checkout/* is "minimal" (a focused
// payment step); it is included per product decision — drop the "/checkout"
// prefix here to restore the nav-less minimal checkout flow.
const PAYMENT_DASHBOARD_WHEN_AUTHED = ["/pricing", "/checkout"] as const;

function stripRouteLangPrefix(path: string): string {
  return path.startsWith("/:lang") ? path.slice("/:lang".length) || "/" : path;
}

function isPaymentDashboardRoute(r: GeneratedRoute): boolean {
  const p = stripRouteLangPrefix(r.path);
  return PAYMENT_DASHBOARD_WHEN_AUTHED.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`),
  );
}

function makeComponent(r: GeneratedRoute) {
  if (r.layout === "auth") {
    const Wrapped = r.component;
    return () => <AuthRouteAdapter Component={Wrapped as React.ComponentType<Record<string, unknown>>} routeName={r.name} />;
  }

  // `/x` and `/:lang/x` share one component identity so switching language
  // keeps the page mounted (see lib/route-identity.ts). Safe because the two
  // variants of a route differ only in `path` / `name` (`-i18n`) / `showInNav`.
  return getStableRouteComponent(r.name, () => buildRoutedPage(r));
}

function buildRoutedPage(r: GeneratedRoute) {
  const PageComponent = r.component;
  const useCommunityShellWrapper = isCommunityShellRoute(r);

  return function RoutedPage() {
    useRouteLanguageSync();

    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Guest mode is only ever active on `authRequired && guestViewAllowed`
    // routes when the token store is empty. Authenticated visitors and
    // truly public routes (no auth required at all) always see isGuest=false
    // so authenticated UI continues to render normally.
    const isGuest =
      Boolean(r.authRequired) &&
      Boolean(r.guestViewAllowed) &&
      !user;

    const handleLogout = React.useCallback(() => {
      logout();
      void navigate({ to: "/login" as unknown as "." } as never);
    }, [logout, navigate]);

    if (isGuest) {
      // Use the public SystemShell variant (no sidebar, no per-user
      // preferences fetch, no dashboard-only side-effects). The shell sees
      // user=null which is intentional — the public header surfaces the
      // login / register CTAs.
      return (
        <GuestModeProvider isGuest>
          <SystemShell
            routes={generatedRoutes}
            currentRoute={r}
            user={null}
            variant="public"
          >
            <GuestBanner />
            {useCommunityShellWrapper ? (
              <CommunityShell>
                <PageComponent />
              </CommunityShell>
            ) : (
              <PageComponent />
            )}
          </SystemShell>
        </GuestModeProvider>
      );
    }

    // Authenticated users get the dashboard shell on payment routes
    // (/pricing, /checkout/*) even though those routes declare a public/minimal
    // layout, so the header/sidebar stay consistent with the rest of the app.
    if (user && isPaymentDashboardRoute(r)) {
      return (
        <GuestModeProvider isGuest={false}>
          <DashboardLayout
            r={r}
            PageComponent={PageComponent}
            useCommunityShellWrapper={useCommunityShellWrapper}
          />
        </GuestModeProvider>
      );
    }

    if (r.layout === "dashboard") {
      return (
        <GuestModeProvider isGuest={false}>
          <DashboardLayout
            r={r}
            PageComponent={PageComponent}
            useCommunityShellWrapper={useCommunityShellWrapper}
          />
        </GuestModeProvider>
      );
    }

    if (r.layout === "public") {
      return (
        <GuestModeProvider isGuest={false}>
          <SystemShell
            routes={generatedRoutes}
            currentRoute={r}
            user={user}
            variant="public"
          >
            <PageComponent />
          </SystemShell>
        </GuestModeProvider>
      );
    }

    return (
      <GuestModeProvider isGuest={false}>
        <PageComponent />
      </GuestModeProvider>
    );
  };
}

const childRoutes = generatedRoutes.map((r) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: toTanstackPath(r.path),
    beforeLoad: async () => {
      await enforcePaymentRouteAvailability(r);
      // Reads the query cache / SSR bootstrap — no fetch per navigation.
      await enforceFeatureRouteAvailability(r, getQueryClient());
      // Route guards read through the platform-aware token store so that
      // native apps (Capacitor) can populate the in-memory access token
      // at cold-start before the first navigation.
      const accessToken = tokenStore.getAccessSync();
      const hasAccessToken = !!accessToken;
      // Auth-gating split:
      //   - private route, no token            → bounce to /login (with ?next=)
      //   - guest-allowed route, no token      → render in guest mode (no
      //     redirect; the component tree picks up `isGuest=true` via
      //     GuestModeProvider injected by makeComponent below)
      //   - any authenticated request          → unchanged
      if (r.authRequired && !hasAccessToken && !r.guestViewAllowed) {
        const path =
          typeof window !== "undefined"
            ? window.location.pathname + window.location.search
            : "";
        const search =
          isSafeReturnPath(path) && path !== "/login"
            ? ({ next: path } as never)
            : undefined;
        throw redirect({
          to: localizedAuthPath(path, "login") as unknown as "/",
          ...(search ? { search } : {}),
        });
      }
      if (AUTH_LANDING_ROUTE_NAMES.has(r.name) && hasAccessToken) {
        throw redirect({ to: getPostLoginDestination() as unknown as "/" });
      }
      if (r.adminOnly) {
        const token = accessToken;
        let isAdmin = false;
        if (token) {
          const base64 = token.split(".")[1];
          if (base64) {
            try {
              const payload = JSON.parse(
                atob(base64.replace(/-/g, "+").replace(/_/g, "/"))
              ) as Record<string, unknown>;
              isAdmin =
                payload.is_staff === true ||
                payload.isStaff === true ||
                payload.is_superuser === true ||
                payload.isSuperuser === true;
            } catch {
              // malformed token — treat as non-admin
            }
          }
        }
        if (!isAdmin) {
          throw redirect({ to: localizedCurrentRoot() as unknown as "/" });
        }
      }
    },
    component: makeComponent(r),
  }),
);

const routeTree = rootRoute.addChildren([
  indexRoute,
  termsRoute,
  localizedTermsRoute,
  privacyRoute,
  localizedPrivacyRoute,
  medicalDisclaimerRoute,
  localizedMedicalDisclaimerRoute,
  docsRoute,
  localizedDocsRoute,
  ...legacyRedirectRoutes,
  localizedIndexRoute,
  ...childRoutes,
]);

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  ...ROUTER_PENDING_OPTIONS,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// ── Mount Capacitor native lifecycle ───────────────────────────────
// Runs only inside the native app binary (iOS / Android). Uses the singleton
// queryClient from QueryProvider (safe to call before React renders).
if (IS_NATIVE) {
  mountNativeLifecycle(
    {
      history: { back: () => router.history.back() },
      navigate: (to: string) => void router.navigate({ to: to as unknown as '.' } as never),
    },
    getQueryClient(),
  );
  // Push notifications: opt-in permission prompt, device registration, and
  // tap-to-deep-link routing. No-op if the user declines permission.
  void initPushNotifications({
    navigate: (to: string) => void router.navigate({ to: to as unknown as '.' } as never),
  });
  // Local notification scheduler: seeds defaults, syncs the OS schedule, and
  // re-syncs on language changes so notification copy stays up-to-date.
  void initScheduler();
}

// ── Web push (browser) ──────────────────────────────────────────────
// Non-native only. Refreshes an already-granted permission without prompting
// (the opt-in prompt lives in Settings → Notifications) and lets the FCM
// service worker drive deep-link navigation on a background notification tap.
if (!IS_NATIVE) {
  void initWebPushIfGranted();
  // GA4 (gtag.js) is installed statically in index.html via Consent Mode v2
  // (loads on every page so Google can detect it; cookieless until opt-in).
  // This only wires LIVE consent changes to gtag (see @shared/lib/native/analytics).
  initAnalytics();
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data as { type?: string; deepLink?: string } | undefined;
      if (data?.type === "push:navigate" && typeof data.deepLink === "string") {
        void router.navigate({ to: data.deepLink as unknown as "." } as never);
      }
    });
  }
}

// ── Render ─────────────────────────────────────────────────────────
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root was not found");
}

function AppRoot() {
  const { status } = useAuth();
  return (
    <RequireAuthProvider isAuthenticated={status === "authenticated"}>
      <RouterProvider router={router} />
    </RequireAuthProvider>
  );
}

if (!rootElement.innerHTML) {
  createRoot(rootElement).render(
    <React.StrictMode>
      <HelmetProvider>
        <LanguageProvider>
          <ThemeProvider>
            <AuthProvider>
              <QueryProvider>
                <AppRoot />
              </QueryProvider>
            </AuthProvider>
          </ThemeProvider>
        </LanguageProvider>
      </HelmetProvider>
    </React.StrictMode>,
  );
}
